import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

interface InvitationData {
  vendor_smart_key: string;
  vendor_name: string;
  shop_name?: string;
  customer_phone: string;
  customer_country_code: string;
  customer_address?: string;
  expires_at: string;
  used_at?: string;
  customer_user_id?: string;
}

interface CustomerProfile {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  phone: string;
  country_code: string;
  address: string;
}

interface InvitationResponse {
  success: boolean;
  data?: any;
  error?: string;
}

const InvitePage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [sessionEstablished, setSessionEstablished] = useState<boolean>(false);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  } | null>(null);


  useEffect(() => {
    const fetchInvitation = async () => {
      if (!code) {
        setError('رمز الدعوة مفقود');
        setLoading(false);
        return;
      }

      try {
        // Extract reset token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const resetTokenParam = urlParams.get('reset_token');

        if (resetTokenParam) {
          const decodedToken = decodeURIComponent(resetTokenParam);

          // The reset token is a Supabase verify URL with query parameters
          try {
            const resetUrl = new URL(decodedToken);
            const queryParams = new URLSearchParams(resetUrl.search);
            const token = queryParams.get('token');
            const tokenType = queryParams.get('type');

            if (!token) {
              console.error('No token found in reset URL');
              setError('رابط إعادة التعيين غير صالح - الرمز مفقود');
              setLoading(false);
              return;
            }

            if (tokenType !== 'recovery') {
              console.error('Wrong token type:', tokenType);
              setError('رابط إعادة التعيين غير صالح - نوع الرمز غير صحيح');
              setLoading(false);
              return;
            }

            // Use verifyOtp to exchange the token for a session
            const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });

            if (sessionError) {
              console.error('Failed to verify OTP token:', sessionError);
              setError('فشل في التحقق من رابط إعادة التعيين: ' + sessionError.message);
              setLoading(false);
              return;
            }

            if (!sessionData.session || !sessionData.user) {
              console.error('Token verified but no user data returned');
              setError('فشل في إنشاء الجلسة - لا توجد بيانات مستخدم');
              setLoading(false);
              return;
            }

            setSessionEstablished(true);
            setSessionUserId(sessionData.user.id);
          } catch (e) {
            console.error('Failed to parse reset token URL:', e);
            setError('فشل في معالجة رابط إعادة التعيين - الرجاء طلب دعوة جديدة');
            setLoading(false);
            return;
          }
        }

        const { data, error: supabaseError } = await supabase
          .from('invitations')
          .select('*')
          .eq('code', code)
          .single();

        if (supabaseError) {
          console.error('Supabase error:', supabaseError);
          setError('دعوة غير صالحة أو منتهية الصلاحية');
          setLoading(false);
          return;
        }

        if (!data) {
          setError('بيانات الدعوة غير متوفرة');
          setLoading(false);
          return;
        }

        const invitationData: InvitationData = data;
        setInvitation(invitationData);

        if (invitationData.customer_user_id) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, username, first_name, last_name, phone, country_code, address')
            .eq('id', invitationData.customer_user_id)
            .single();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
          } else if (profileData) {
            setCustomerProfile(profileData);
            setFirstName(profileData.first_name || '');
            setLastName(profileData.last_name || '');
            setAddress(profileData.address || invitationData.customer_address || '');
          }
        } else {
          if (invitationData.customer_address) {
            setAddress(invitationData.customer_address);
          }
        }

        if (invitationData.expires_at) {
          calculateTimeRemaining();
          const interval = setInterval(() => {
            calculateTimeRemaining();
          }, 1000);

          return () => clearInterval(interval);
        }

      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('حدث خطأ أثناء جلب بيانات الدعوة');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [code]);

  const calculateTimeRemaining = () => {
    try {
      const now = new Date().getTime();
      // Always show 48 hours for UX purposes (creates urgency without actual expiry)
      const difference = 48 * 60 * 60 * 1000; // 48 hours in milliseconds
      
      if (difference <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true
        });
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeRemaining({
        days,
        hours,
        minutes,
        seconds,
        expired: false
      });
    } catch (error) {
      console.error('Error calculating time remaining:', error);
    }
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invitation) {
      setRegistrationError('بيانات الدعوة غير متوفرة');
      return;
    }

    if (password !== confirmPassword) {
      setRegistrationError('كلمات المرور غير متطابقة');
      return;
    }

    if (password.length < 6) {
      setRegistrationError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setRegistrationLoading(true);
    setRegistrationError(null);

    try {
      if (customerProfile && invitation.customer_user_id) {
        if (!sessionEstablished) {
          console.error('Session not established from recovery token');
          setRegistrationError('رابط إعادة التعيين مفقود أو منتهي الصلاحية. الرجاء طلب دعوة جديدة.');
          setRegistrationLoading(false);
          return;
        }

        // Check if we have an active session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (!session) {
          console.error('No active session found');
          setRegistrationError('الجلسة غير نشطة. الرجاء المحاولة مرة أخرى');
          setRegistrationLoading(false);
          return;
        }

        // Validate that the session user matches the invitation's customer
        if (session.user.id !== invitation.customer_user_id) {
          console.error('User mismatch - Session user:', session.user.id, 'Expected:', invitation.customer_user_id);
          setRegistrationError('خطأ في التحقق من الهوية. الجلسة لا تطابق المستخدم المدعو.');
          setRegistrationLoading(false);
          return;
        }

        // Update the password with the active session
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password
        });

        if (passwordError) {
          console.error('Password update error:', passwordError);
          setRegistrationError('فشل في تحديث كلمة المرور: ' + passwordError.message);
          setRegistrationLoading(false);
          return;
        }

        const updateData: any = {};
        if (firstName && firstName !== customerProfile.first_name) {
          updateData.first_name = firstName;
        }
        if (lastName && lastName !== customerProfile.last_name) {
          updateData.last_name = lastName;
        }
        if (address && address !== customerProfile.address) {
          updateData.address = address;
        }
        if (firstName || lastName) {
          updateData.username = `${firstName || customerProfile.first_name || ''} ${lastName || customerProfile.last_name || ''}`.trim();
        }

        if (Object.keys(updateData).length > 0) {
          const { error: profileError } = await supabase
            .from('profiles')
            .update(updateData)
            .eq('id', customerProfile.id);

          if (profileError) {
            console.error('Profile update error:', profileError);
          }
        }

        const { error: invitationUpdateError } = await supabase
          .from('invitations')
          .update({ used_at: new Date().toISOString() })
          .eq('code', code);

        if (invitationUpdateError) {
          console.error('Error marking invitation as used:', invitationUpdateError);
        }

        navigate('/login', { replace: true });
      } else {
        // Legacy flow: Creating new user
        const email = `${invitation.customer_phone}@ajel.life`;

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: undefined,
          }
        });

        if (authError || !authData.user) {
          console.error('Auth error:', authError);
          setRegistrationError(authError?.message || 'فشل في إنشاء الحساب');
          setRegistrationLoading(false);
          return;
        }

        const profileData = {
          id: authData.user.id,
          username: `${firstName} ${lastName}`.trim() || invitation.customer_phone,
          first_name: firstName,
          last_name: lastName,
          phone: invitation.customer_phone,
          country_code: invitation.customer_country_code,
          address: address || invitation.customer_address || '',
          account_type: 'customer',
          reference_smart_key: invitation.vendor_smart_key
        };

        const { error: profileError } = await supabase
          .from('profiles')
          .insert(profileData);

        if (profileError) {
          console.error('Profile creation error:', profileError);
          setRegistrationError('فشل في إنشاء الملف الشخصي: ' + profileError.message);
          setRegistrationLoading(false);
          return;
        }

        try {
          await supabase.rpc('use_invitation', {
            p_code: code,
            p_customer_user_id: authData.user.id
          });
        } catch (invitationError) {
          console.error('Error in invitation processing:', invitationError);
        }

        navigate('/home', { replace: true });
      }
    } catch (err) {
      console.error('Registration error:', err);
      setRegistrationError(err instanceof Error ? err.message : 'حدث خطأ أثناء التسجيل');
    } finally {
      setRegistrationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-3xl shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F87171] mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل بيانات الدعوة...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white p-8 rounded-3xl shadow-lg max-w-md w-full text-center relative overflow-hidden border border-purple-100">
          <div className="absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-70"></div>
          <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-70"></div>
          <div className="relative">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">خطأ في الدعوة</h2>
            <p className="text-gray-600 mb-6">{error || 'دعوة غير صالحة'}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-md hover:shadow-lg"
              >
                إعادة تحميل الصفحة
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                العودة للرئيسية
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 p-6 flex flex-col items-center justify-center">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="w-full max-w-md bg-white rounded-3xl shadow-lg p-8 relative overflow-hidden border border-purple-100">
        {/* Decorative elements */}
        <div className="absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-70"></div>
        <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-70"></div>
        
        <div className="relative text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">✨</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
            {customerProfile ? 'مرحباً بك!' : 'مرحباً بك!'}
          </h2>
          <p className="text-gray-600">
            {customerProfile ? (
              <>
                تم دعوتك من قبل <span className="font-semibold text-purple-600">{invitation.vendor_name}</span>
                {invitation.shop_name && <span> - {invitation.shop_name}</span>}
              </>
            ) : (
              <>
                تم دعوتك من قبل <span className="font-semibold text-purple-600">{invitation.vendor_name}</span>
                {invitation.shop_name && <span> - {invitation.shop_name}</span>}
              </>
            )}
          </p>
          <p className="text-gray-600 mt-2">
            {customerProfile ? (
              'تم إنشاء حسابك مسبقاً. قم باختيار كلمة مرور قوية لتأمين حسابك وابدأ الاستخدام مباشرة'
            ) : (
              'أدخل بياناتك لإنشاء حسابك الآن واستمتع بمتابعة مبيعاتك ومشترياتك الآجلة بكل المميزات مجاناً'
            )}
          </p>
          
          {timeRemaining && (
            <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-700 text-xs text-center font-medium mb-1">
                أكمل التسجيل خلال:
              </p>
              <div className="flex justify-center space-x-2 text-center">
                <div className="bg-amber-100 px-2 py-1 rounded text-amber-800 text-xs w-14">
                  <span className="block font-bold">{timeRemaining.days}</span>
                  <span className="text-xs">يوم</span>
                </div>
                <div className="bg-amber-100 px-2 py-1 rounded text-amber-800 text-xs w-14">
                  <span className="block font-bold">{timeRemaining.hours}</span>
                  <span className="text-xs">ساعة</span>
                </div>
                <div className="bg-amber-100 px-2 py-1 rounded text-amber-800 text-xs w-14">
                  <span className="block font-bold">{timeRemaining.minutes}</span>
                  <span className="text-xs">دقيقة</span>
                </div>
                <div className="bg-amber-100 px-2 py-1 rounded text-amber-800 text-xs w-14">
                  <span className="block font-bold">{timeRemaining.seconds}</span>
                  <span className="text-xs">ثانية</span>
                </div>
              </div>
            </div>
          )}

          {customerProfile && invitation.customer_user_id && (
            <div className={`mt-3 p-2 rounded-lg border ${sessionEstablished ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-xs text-center font-medium ${sessionEstablished ? 'text-green-700' : 'text-gray-600'}`}>
                {sessionEstablished ? '✓ تم التحقق من الهوية بنجاح' : 'جاري التحقق من الهوية...'}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleRegistration} className="space-y-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              الاسم الأول {!customerProfile && '*'}
            </label>
            <input
              id="firstName"
              type="text"
              required={!customerProfile}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              placeholder="أدخل الاسم الأول"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              الاسم الأخير {!customerProfile && '*'}
            </label>
            <input
              id="lastName"
              type="text"
              required={!customerProfile}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              placeholder="أدخل الاسم الأخير"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              رقم الهاتف
            </label>
            <div className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-100 flex items-center justify-between">
              <span className="text-gray-500 text-sm">(لا يمكن تعديله)</span>
              <span className="font-mono" dir="ltr">{invitation.customer_country_code + " " + invitation.customer_phone}</span>
            </div>
          </div>

          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              العنوان
            </label>
            <input
              type="text"
              id="address"
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              placeholder="أدخل عنوانك"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              كلمة المرور
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              placeholder="أدخل كلمة المرور"
              minLength={6}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              تأكيد كلمة المرور
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              placeholder="أعد إدخال كلمة المرور"
              minLength={6}
            />
          </div>

          {registrationError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 shadow-sm">
              <div className="flex">
                <div className="text-red-500 text-lg mr-2">❌</div>
                <div className="text-red-600">{registrationError}</div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={registrationLoading}
            className="w-full py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex justify-center items-center"
          >
            {registrationLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                {customerProfile ? 'جاري إعداد الحساب...' : 'جاري التسجيل...'}
              </div>
            ) : (
              customerProfile ? 'حفظ ومتابعة' : 'إنشاء الحساب'
            )}
          </button>
        </form>

        {/* Celebratory bottom element */}
        <div className="mt-8 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-purple-100 shadow-sm">
          <p className="text-center text-sm text-blue-800">
            انضم إلى أكثر من 100,000+ مستخدم يديرون أعمالهم بسهولة مع آجل 🚀
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvitePage;